import { Component, type ErrorInfo, type ReactNode } from "react";
import { logger } from "@/utils/logger";
import { logError } from "@/utils/errorTracking";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary - React error boundary component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 *
 * Features:
 * - Catches errors in child components
 * - Displays user-friendly error message
 * - Provides retry functionality
 * - Shows technical details in expandable section
 * - Accessible error announcements (role="alert")
 *
 * @example
 * <ErrorBoundary fallback={<CustomError />} onRetry={handleRetry}>
 *   <App />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error("Error caught by boundary:", error, errorInfo);
    // Send to error tracking service (Sentry)
    logError(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    });
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
    });
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-[#09091e] flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            <div role="alert" aria-live="assertive">
              <h1 className="text-4xl font-bold text-white mb-4">Oops!</h1>
              <p className="text-lg text-[rgba(255,255,255,0.6)] mb-8">
                Something went wrong. Please try refreshing the page.
              </p>
            </div>
            <div className="flex gap-4 justify-center">
              <button
                onClick={this.handleRetry}
                className="bg-white text-[#09091e] px-6 py-3 rounded-full font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#09091e]"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-[rgba(255,255,255,0.1)] text-white px-6 py-3 rounded-full font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#09091e]"
              >
                Refresh Page
              </button>
            </div>
            {this.state.error && (
              <details className="mt-8 text-left">
                <summary className="text-[rgba(255,255,255,0.4)] cursor-pointer hover:text-[rgba(255,255,255,0.6)]">
                  Technical Details
                </summary>
                <pre className="mt-4 p-4 bg-[rgba(255,255,255,0.05)] rounded text-xs text-[rgba(255,255,255,0.6)] overflow-auto">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
